import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Box,
  Tooltip,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
import { Menu as MenuIcon, AccountCircle, Notifications } from '@mui/icons-material';
import { useEffect, memo, useMemo, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useUserScope } from '../../hooks/useUserScope';
import { useQuery } from '@tanstack/react-query';
import { UserService, UserInfo } from '../../services/userService';

interface AdminAppBarProps {
  onMenuClick: () => void;
  isMobile: boolean;
}

export const AdminAppBar = memo(({ onMenuClick, isMobile }: AdminAppBarProps) => {
  const { user, signOut, isAdminFlag } = useAuth();
  const { globalUserId, setGlobalUserId, overrideUserId, clearOverrides, effectiveUserId } =
    useUserScope();

  const {
    data: usersData,
    isError,
    error,
  } = useQuery({
    queryKey: ['users', 'scope-selector'],
    queryFn: UserService.getAllUsers,
    enabled: isAdminFlag,
    staleTime: 60_000,
  });

  // Log errors for debugging but don't show toast (this is not critical functionality)
  useEffect(() => {
    if (isError && error) {
      console.error('Failed to load users for scope selector:', error);
    }
  }, [isError, error]);

  const users: UserInfo[] = useMemo(() => usersData?.users || [], [usersData]);
  const selectedUser = useMemo(
    () => users.find((u) => u.uid === globalUserId) || null,
    [users, globalUserId]
  );
  const overrideActive = useMemo(() => !!overrideUserId, [overrideUserId]);

  const handleUserChange = useCallback(
    (_: unknown, value: UserInfo | null) => {
      setGlobalUserId(value ? value.uid : null);
    },
    [setGlobalUserId]
  );

  const handleSignOut = useCallback(() => {
    signOut();
  }, [signOut]);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      color="default"
      sx={{
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      <Toolbar>
        {isMobile && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={onMenuClick}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1.125rem' }}
        >
          Admin
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {isAdminFlag && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 3, minWidth: 260 }}>
            <Autocomplete
              size="small"
              value={selectedUser}
              onChange={handleUserChange}
              options={users}
              getOptionLabel={(u) => UserService.formatDisplayName(u)}
              sx={{ width: 240 }}
              renderInput={(params) => <TextField {...params} label="Global Scope" />}
            />
            {overrideActive && (
              <Chip label="Override" color="warning" size="small" onDelete={clearOverrides} />
            )}
            {effectiveUserId && effectiveUserId !== user?.uid && !overrideActive && (
              <Chip label="Impersonating" color="info" size="small" />
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {user && (
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Typography
                variant="body2"
                sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.875rem' }}
              >
                {user.displayName || user.email?.split('@')[0]}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="medium"
              color="inherit"
              sx={{ color: 'text.secondary', '&:hover': { backgroundColor: 'grey.100' } }}
            >
              <Notifications />
            </IconButton>
            <Tooltip title={user?.email || ''}>
              <IconButton
                size="medium"
                color="inherit"
                onClick={handleSignOut}
                sx={{ color: 'text.secondary', '&:hover': { backgroundColor: 'grey.100' } }}
              >
                {user?.photoURL ? (
                  <Avatar src={user.photoURL} sx={{ width: 32, height: 32 }} />
                ) : (
                  <AccountCircle />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
});
